import { Fragment, useMemo, useRef, useState } from 'react';
import { Text } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import type { EmissionReportData, EmissionReportMassFlow, EmissionReportMassFlowEntry } from '../../types/entities';
import { EMISSION_INPUT_PROPS, handleDecimalKey } from '../form/numberFieldPresets.js';
import { ValidatedNumberInput } from '../form/validatedFields.js';
import { importableEmissionGroups } from './importableEmissionFields.js';
import { cellText, cellTitle, type CellEdit, decimalText, isMassFlowChanged, setMassFlowCell } from './matrixEdits.js';
import { useMassFlowAverages } from './useMassFlowAverages.js';
import classes from './EmissionReportMatrix.module.css';

/**
 * Der Vorschau-Abschnitt "Massenströme für die Frachtenberechnung" (Phase 40, Plan 40-09, D-17):
 * je importierter Komponente die Einzelwerte (mit Datum und Betriebszustand) und die vom Server
 * gebildeten Mittel je Betriebszustand, unter der Messungsmatrix in derselben Vorschau -- EIN
 * Upload, EINE Vorschau, EIN "Übernehmen" (kein zweiter Abruf, kein zweiter Speicherweg).
 *
 * Fuer diesen Abschnitt gibt es KEINEN eigenen Sketch (Sketch 024 behandelt nur das Rechenblatt,
 * Sketch 020 die Messungsmatrix) -- er uebernimmt deshalb bewusst die Optik und die Klassen aus
 * `EmissionReportMatrix.module.css` (keine neue CSS-Datei) und wird in der Browserabnahme (40-10)
 * mit Messwerten geprueft; weicht der Nutzer dort ab, ist das eine neue Entscheidung, keine
 * Nacharbeit dieses Plans.
 *
 * Zellbearbeitung nach demselben Grundsatz wie `EmissionReportMatrix.tsx` (Schnappschuss vor der
 * Bearbeitung, commit/cancel), aber ueber die DOKUMENT-Ebene: `setMassFlowCell` liefert das
 * gesamte neue `EmissionReportData` (nicht nur eine Zelle), der Schnappschuss haelt deshalb den
 * VOLLEN Dokumentstand vor der laufenden Bearbeitung fest -- genau das braucht `setMassFlowCell`
 * fuer seine "war schon leer"-Regel (siehe `matrixEdits.ts`). Derselbe volle-Dokument-Ansatz wie
 * bei `removeMeasurement`/`restoreMeasurement`, die die aufrufende Seite bereits per
 * `form.setValues(...)` committet.
 *
 * KEINE Pfeiltasten-Navigation (anders als die Matrix, Punkt 9 dort) -- jede Zelle ist per Tab
 * erreichbar (`tabIndex 0`) und oeffnet mit Enter den Editor, wie im Plan-Constraint vorgesehen.
 */

function getMassFlows(data: EmissionReportData): Record<string, EmissionReportMassFlow> {
  return Array.isArray(data.massFlows) ? {} : (data.massFlows ?? {});
}

/** `2026-03-10` -> `10.03.`, dieselbe Zeichenketten-Zerlegung wie in `EmissionReportMatrix.tsx`
 *  (kein `new Date(iso)`: zeigt in Zonen mit negativem Offset den Vortag). */
function formatEntryDate(entry: EmissionReportMassFlowEntry): string {
  if (entry.date) {
    const [, month, day] = entry.date.slice(0, 10).split('-');
    return day && month ? `${day}.${month}.` : entry.date;
  }
  return entry.dateRaw ?? '—';
}

type EditTarget = { componentKey: string; entryIndex: number };

function editKey(target: EditTarget): string {
  return `${target.componentKey}:${target.entryIndex}`;
}

export interface MassFlowPreviewSectionProps {
  /** An `resultData` gebundenes Formular (Modus `controlled`), vom Aufrufer erzeugt. */
  form: UseFormReturnType<EmissionReportData>;
  /** Der unveraenderte Originalstand -- einzige Quelle der Aenderungsmarkierung (D-05, wie in der Matrix). */
  sourceData: EmissionReportData;
  /** Nach dem Uebernehmen: reiner Text, kein Eingabefeld -- Mittel bleiben die eingefrorenen. */
  readOnly: boolean;
}

export function MassFlowPreviewSection({ form, sourceData, readOnly }: MassFlowPreviewSectionProps) {
  const [editing, setEditing] = useState<EditTarget | null>(null);
  // Schnappschuss des VOLLEN Dokuments vor der aktuellen Bearbeitung -- siehe Kopfkommentar.
  const snapshotRef = useRef<{ key: string; data: EmissionReportData } | null>(null);

  const values = form.getValues();
  const massFlows = getMassFlows(values);
  const massFlowKeys = useMemo(() => Object.keys(massFlows), [massFlows]);

  const rowGroups = useMemo(() => importableEmissionGroups(massFlowKeys), [massFlowKeys]);

  // T-40-26: kein Netzaufruf im Protokollmodus -- dort steht ohnehin nur der eingefrorene Stand.
  const averages = useMassFlowAverages(values.massFlows ?? [], !readOnly);

  function startEdit(componentKey: string, entryIndex: number) {
    if (readOnly) return;
    snapshotRef.current = { key: editKey({ componentKey, entryIndex }), data: form.getValues() };
    setEditing({ componentKey, entryIndex });
  }

  function commitEdit(componentKey: string, entryIndex: number) {
    const key = editKey({ componentKey, entryIndex });
    const current = form.getValues();
    const currentEntry = getMassFlows(current)[componentKey]?.entries[entryIndex];
    const edit: CellEdit = { value: currentEntry?.value ?? null, belowLimit: currentEntry?.belowLimit ?? false };
    const baseData = snapshotRef.current?.key === key ? snapshotRef.current.data : current;
    form.setValues(setMassFlowCell(baseData, componentKey, entryIndex, edit));
    snapshotRef.current = null;
    setEditing(null);
  }

  function cancelEdit(componentKey: string, entryIndex: number) {
    const key = editKey({ componentKey, entryIndex });
    if (snapshotRef.current?.key === key) {
      form.setValues(snapshotRef.current.data);
    }
    snapshotRef.current = null;
    setEditing(null);
  }

  const heading = (
    <div className={classes.mfHead}>
      <Text component="h2" className={classes.mxHeadTitle} mb={2}>
        Massenströme für die Frachtenberechnung
      </Text>
      <Text size="sm" c="dimmed">
        Einzelwerte je Komponente in der Einheit des Rechenblatts. Die Mittelwerte je
        Betriebszustand bildet der Server; Werte unter der Bestimmungsgrenze zählen mit dem halben
        Wert. Beim Übernehmen werden sie im Rechenblatt des Ofens vorbelegt.
      </Text>
    </div>
  );

  if (massFlowKeys.length === 0) {
    return (
      <div className={classes.mfWrap}>
        {heading}
        <Text size="sm" c="dimmed" className={classes.mfEmpty}>
          Der Bericht enthält keine Massenströme der importierten Komponenten.
        </Text>
      </div>
    );
  }

  const columnCount = 5; // Komponente + Einzelwerte + Ø Verbundbetrieb + Ø Direktbetrieb + Einheit

  return (
    <div className={classes.mfWrap}>
      {heading}
      <table className={`${classes.mx} ${classes.mfTable}`}>
        <thead>
          <tr>
            <th className={classes.fl}>Komponente</th>
            <th className={`${classes.mc} ${classes.mfEntries}`}>Einzelwerte</th>
            <th className={classes.mc}>Ø Verbundbetrieb</th>
            <th className={classes.mc}>Ø Direktbetrieb</th>
            <th className={classes.un}>Einheit</th>
          </tr>
        </thead>
        <tbody>
          {rowGroups.map((group) => (
            <Fragment key={group.title}>
              <tr className={classes.gr}>
                <td colSpan={columnCount}>
                  <span className={classes.grLabel}>{group.title}</span>
                </td>
              </tr>
              {group.fields.map((field) => {
                const flow = massFlows[field.key];
                const entries = flow?.entries ?? [];
                const average = averages[field.key];
                return (
                  <tr key={field.key}>
                    <td className={`${classes.fl} ${classes.cellLabel}`}>{field.label}</td>
                    <td className={classes.cell}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {entries.map((entry, entryIndex) => {
                          const active =
                            !readOnly &&
                            editing !== null &&
                            editing.componentKey === field.key &&
                            editing.entryIndex === entryIndex;
                          const changed = isMassFlowChanged(sourceData, values, field.key, entryIndex);
                          return (
                            <div
                              key={entryIndex}
                              title={active ? undefined : cellTitle(entry)}
                              tabIndex={readOnly ? undefined : 0}
                              role={readOnly ? undefined : 'button'}
                              className={[
                                classes.cell,
                                !readOnly && classes.editable,
                                entry.value === null && !active && classes.empty,
                                changed && !active && classes.edited,
                              ]
                                .filter(Boolean)
                                .join(' ')}
                              style={{ position: 'relative', minWidth: 80, padding: '5px 9px' }}
                              onClick={() => {
                                if (!active) startEdit(field.key, entryIndex);
                              }}
                              onKeyDown={(event) => {
                                if (event.target !== event.currentTarget) return;
                                if (event.key === 'Enter') {
                                  event.preventDefault();
                                  if (!active) startEdit(field.key, entryIndex);
                                }
                              }}
                            >
                              <span className={active ? classes.ghost : undefined}>{cellText(entry)}</span>
                              {!active && (
                                <span className={classes.md}>
                                  {formatEntryDate(entry)} {entry.operatingMode ?? '—'}
                                </span>
                              )}
                              {active && (
                                <div className={classes.cellEditor}>
                                  <ValidatedNumberInput
                                    form={form}
                                    field={`massFlows.${field.key}.entries.${entryIndex}.value`}
                                    belowLimitField={`massFlows.${field.key}.entries.${entryIndex}.belowLimit`}
                                    kind="decimal"
                                    align="right"
                                    size="xs"
                                    autoFocus
                                    {...EMISSION_INPUT_PROPS}
                                    styles={{
                                      root: { height: '100%' },
                                      wrapper: { height: '100%' },
                                      input: { height: '100%', minHeight: 0 },
                                    }}
                                    onBlur={() => commitEdit(field.key, entryIndex)}
                                    onKeyDown={(event) => {
                                      handleDecimalKey(event);
                                      if (event.key === 'Enter') {
                                        event.preventDefault();
                                        commitEdit(field.key, entryIndex);
                                      }
                                      if (event.key === 'Escape') {
                                        event.preventDefault();
                                        cancelEdit(field.key, entryIndex);
                                      }
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </td>
                    <td className={`${classes.cell} ${classes.mc}`}>{average?.averageCompound != null ? decimalText(average.averageCompound) : '—'}</td>
                    <td className={`${classes.cell} ${classes.mc}`}>{average?.averageDirect != null ? decimalText(average.averageDirect) : '—'}</td>
                    <td className={classes.un}>{flow?.unit ?? ''}</td>
                  </tr>
                );
              })}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
